import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Rating,
  TextField,
  Alert,
  CircularProgress,
} from "@mui/material";
import {
  Visibility as VisibilityIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  CheckCircle as CheckCircleIcon,
} from "@mui/icons-material";
import { maintenanceAPI } from "../../utils/api";

const MyMaintenanceRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [viewDialog, setViewDialog] = useState(false);
  const [satisfactionDialog, setSatisfactionDialog] = useState(false);
  const [submittingFeedback, setSubmittingFeedback] = useState(false);
  const [satisfaction, setSatisfaction] = useState({
    rating: 5,
    feedback: "",
  });

  useEffect(() => {
    fetchMyRequests();
  }, []);

  const fetchMyRequests = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await maintenanceAPI.getMyRequests();
      setRequests(response.data);
    } catch (error) {
      console.error("Error fetching maintenance requests:", error);
      setError("Failed to load maintenance requests");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitSatisfaction = async () => {
    if (!selectedRequest) return;

    setSubmittingFeedback(true);
    try {
      await maintenanceAPI.closeRequest(selectedRequest.id, {
        satisfactionRating: satisfaction.rating,
        satisfactionFeedback: satisfaction.feedback,
      });

      // Refresh the requests list
      await fetchMyRequests();

      // Close dialog and reset form
      setSatisfactionDialog(false);
      setSatisfaction({ rating: 5, feedback: "" });
      setSelectedRequest(null);
    } catch (error) {
      console.error("Error submitting satisfaction feedback:", error);
      setError("Failed to submit satisfaction feedback");
    } finally {
      setSubmittingFeedback(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "pending":
        return "warning";
      case "in_progress":
        return "info";
      case "resolved":
        return "success";
      case "rejected":
        return "error";
      case "closed":
        return "default";
      default:
        return "default";
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case "pending":
        return "Pending";
      case "in_progress":
        return "In Progress";
      case "resolved":
        return "Resolved";
      case "rejected":
        return "Rejected";
      case "closed":
        return "Closed";
      default:
        return status;
    }
  };

  const handleViewRequest = (request) => {
    setSelectedRequest(request);
    setViewDialog(true);
  };

  const handleCloseRequest = async () => {
    setSatisfactionDialog(true);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        My Maintenance Requests
      </Typography>

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Loading State */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Token #</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Location</TableCell>
                <TableCell>Priority</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {requests.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No maintenance requests found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                requests.map((request) => (
                  <TableRow key={request.id}>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {request.tokenNumber}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={request.category}
                        variant="outlined"
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{request.location}</TableCell>
                    <TableCell>
                      <Chip
                        label={request.priority}
                        color={
                          request.priority === "urgent" ||
                          request.priority === "high"
                            ? "error"
                            : request.priority === "medium"
                            ? "warning"
                            : "default"
                        }
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getStatusLabel(request.status)}
                        color={getStatusColor(request.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{formatDate(request.createdAt)}</TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => handleViewRequest(request)}
                      >
                        <VisibilityIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* View Request Dialog */}
      <Dialog
        open={viewDialog}
        onClose={() => setViewDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Request Details - {selectedRequest?.tokenNumber}
        </DialogTitle>
        <DialogContent>
          {selectedRequest && (
            <Box sx={{ pt: 2 }}>
              <Typography variant="h6" gutterBottom>
                {selectedRequest.category} - {selectedRequest.location}
              </Typography>
              <Typography variant="body2" paragraph>
                {selectedRequest.description}
              </Typography>

              <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                <Chip
                  label={`Priority: ${selectedRequest.priority}`}
                  color={
                    selectedRequest.priority === "urgent" ||
                    selectedRequest.priority === "high"
                      ? "error"
                      : selectedRequest.priority === "medium"
                      ? "warning"
                      : "default"
                  }
                />
                <Chip
                  label={`Status: ${getStatusLabel(selectedRequest.status)}`}
                  color={getStatusColor(selectedRequest.status)}
                />
              </Box>

              {selectedRequest.assignedWorker && (
                <Paper sx={{ p: 2, mb: 2, bgcolor: "grey.50" }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Assigned Worker
                  </Typography>
                  <Typography variant="body2">
                    {selectedRequest.assignedWorker.name}
                  </Typography>
                  <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
                    <IconButton
                      size="small"
                      href={`tel:${selectedRequest.assignedWorker.phone}`}
                    >
                      <PhoneIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      href={`mailto:${selectedRequest.assignedWorker.email}`}
                    >
                      <EmailIcon />
                    </IconButton>
                  </Box>
                </Paper>
              )}

              {selectedRequest.scheduledDate && (
                <Typography variant="body2" color="text.secondary">
                  Scheduled: {formatDate(selectedRequest.scheduledDate)}
                </Typography>
              )}

              {selectedRequest.completedAt && (
                <Typography variant="body2" color="text.secondary">
                  Completed: {formatDate(selectedRequest.completedAt)}
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {selectedRequest?.status === "resolved" && (
            <Button
              onClick={handleCloseRequest}
              color="success"
              variant="contained"
              startIcon={<CheckCircleIcon />}
            >
              Mark as Satisfied & Close
            </Button>
          )}
          <Button onClick={() => setViewDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Satisfaction Dialog */}
      <Dialog
        open={satisfactionDialog}
        onClose={() => setSatisfactionDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Rate Your Experience</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="body2" gutterBottom>
              How satisfied are you with the maintenance service?
            </Typography>
            <Rating
              value={satisfaction.rating}
              onChange={(event, newValue) =>
                setSatisfaction({ ...satisfaction, rating: newValue })
              }
              size="large"
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Feedback (Optional)"
              value={satisfaction.feedback}
              onChange={(e) =>
                setSatisfaction({ ...satisfaction, feedback: e.target.value })
              }
              placeholder="Share your experience..."
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSatisfactionDialog(false)}>Cancel</Button>
          <Button
            onClick={handleSubmitSatisfaction}
            variant="contained"
            color="success"
            disabled={submittingFeedback}
          >
            {submittingFeedback ? "Submitting..." : "Submit & Close Request"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MyMaintenanceRequests;
